import {
  type OverallProgress,
  type StageProgress,
  formatStepDuration,
} from '@/lib/odmStages'
import { cn } from '@/lib/utils'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'

import { type Accent, Badge, ProgressBar } from '@droidmap/frontend-ui'
import { MappingJobStepStatus } from '@droidmap/mapping-job-service-contract'

export interface StageTimelineProps {
  stages: StageProgress[]
  overall: OverallProgress
  className?: string
}

const STATUS_TONE: Record<MappingJobStepStatus, Accent> = {
  [MappingJobStepStatus.COMPLETED]: 'success',
  [MappingJobStepStatus.RUNNING]: 'warning',
  [MappingJobStepStatus.FAILED]: 'danger',
  [MappingJobStepStatus.PENDING]: 'neutral',
}

const STATUS_LABEL: Record<MappingJobStepStatus, string> = {
  [MappingJobStepStatus.COMPLETED]: 'Done',
  [MappingJobStepStatus.RUNNING]: 'Running',
  [MappingJobStepStatus.FAILED]: 'Failed',
  [MappingJobStepStatus.PENDING]: 'Pending',
}

/** The connector node on the left rail — a check, cross, spinner ring or dot. */
function StageNode({ status }: { status: MappingJobStepStatus }) {
  if (status === MappingJobStepStatus.COMPLETED) {
    return (
      <span className="bg-success/15 text-success grid h-6 w-6 place-items-center rounded-full">
        <CheckIcon className="h-3.5 w-3.5" />
      </span>
    )
  }
  if (status === MappingJobStepStatus.FAILED) {
    return (
      <span className="bg-danger/15 text-danger grid h-6 w-6 place-items-center rounded-full">
        <XMarkIcon className="h-3.5 w-3.5" />
      </span>
    )
  }
  if (status === MappingJobStepStatus.RUNNING) {
    return (
      <span className="border-warning/30 border-t-warning grid h-6 w-6 animate-spin place-items-center rounded-full border-2" />
    )
  }
  return (
    <span className="bg-bg-2 border-border grid h-6 w-6 place-items-center rounded-full border">
      <span className="bg-text-mute h-1.5 w-1.5 rounded-full" />
    </span>
  )
}

/** Duration cell: elapsed for a running stage, total for a finished one. */
function StageDuration({ stage }: { stage: StageProgress }) {
  const text = formatStepDuration(stage.durationMs)
  if (!text) {
    return <span className="text-text-mute font-mono text-[11px]">—</span>
  }
  const running = stage.status === MappingJobStepStatus.RUNNING
  return (
    <span
      className={cn(
        'font-mono text-[11px]',
        running ? 'text-warning' : 'text-text-dim',
      )}
    >
      {running ? `${text} elapsed` : text}
    </span>
  )
}

/**
 * Renders an ODM job's per-stage progress: an overall completion bar plus a
 * compact grid of stage tiles. Purely presentational — the container feeds it
 * ordered {@link StageProgress} rows and a derived {@link OverallProgress}.
 */
export function StageTimeline({
  stages,
  overall,
  className,
}: StageTimelineProps) {
  const failedShare = stages.some(
    (s) => s.status === MappingJobStepStatus.FAILED,
  )
    ? Math.max(100 / Math.max(stages.length, 1), 2)
    : 0

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Overall progress */}
      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-text-mute font-mono text-[10px] uppercase tracking-[0.16em]">
            Overall progress
          </span>
          <span className="text-text font-mono text-[12px]">
            {overall.completed}/{overall.total} stages · {overall.percent}%
          </span>
        </div>
        <ProgressBar
          currentProgress={overall.percent}
          errorProgress={failedShare}
        />
      </div>

      {/* Stage tiles */}
      <ol className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
        {stages.map((stage) => (
          <li
            key={stage.name}
            className={cn(
              'border-border bg-bg-2 flex items-center gap-3 rounded-md border px-3 py-2.5',
              stage.status === MappingJobStepStatus.RUNNING &&
                'border-warning/40',
              stage.status === MappingJobStepStatus.FAILED &&
                'border-danger/40',
            )}
          >
            <StageNode status={stage.status} />
            <div className="min-w-0 flex-1">
              <div className="text-text truncate text-[13px] font-medium">
                {stage.label}
              </div>
              <StageDuration stage={stage} />
            </div>
            <Badge
              tone={STATUS_TONE[stage.status] ?? 'neutral'}
              size="sm"
              dot
              pulse={stage.status === MappingJobStepStatus.RUNNING}
            >
              {STATUS_LABEL[stage.status] ?? stage.status}
            </Badge>
          </li>
        ))}
      </ol>
    </div>
  )
}
